import { AxiosResponse } from "axios";
import moment from "moment";
import { useEffect, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import RequisitionType from "../../../Services/Requisition/RequisitionTypeService";
import DatePickerDuplicate from "../../../Shared/Common/DatePicker/DatePickerDuplicate";
import { reactSelectSMStyle, styles } from "../../../Utils/Common";
import BreadCrumbs from "../../../Utils/Common/Breadcrumb";
import {
  GetTestsLookup,
  GetTOXAndBloodInstrumentsLookup,
  LeveyJenningDownloadReport,
  QCLevelsLookup,
} from "Services/ToxLeveyJennings";
import PermissionComponent, { getPermissionDisplayName } from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { StringRecord } from "Shared/Type";
import TestsSelection from "Pages/LIS/ToxLeveyJenning/TestsSelection";
import { validateFields } from "Pages/LIS/Toxicology/CommonFunctions";

interface ToxLeveyJenningsValues {
  fromDate: string;
  toDate: string;
  reqTypeId: number;
  instrumentId: number;
  panelTypeId: number;
  specimenTypeId: number;
  qcLevelIds: number[];
  testConfigIds: number[];
}
const BloodLeveyJenning = () => {
  const { t } = useLang();

  const initialToxLeveyJenning = {
    fromDate: "",
    toDate: "",
    reqTypeId: 0,
    instrumentId: 0,
    panelTypeId: 0,
    specimenTypeId: 0,
    qcLevelIds: [],
    testConfigIds: [],
  };

  const queryDisplayTagNames: StringRecord = {
    fromDate: "Start Date",
    toDate: "End Date",
    reqTypeId: "Requisition Type",
    instrumentId: "Instrument",
    panelTypeId: "Test Type",
    specimenTypeId: "Specimen Types",
    qcLevelIds: "QC Level",
    testConfigIds: "Selected Analytes",
  };

  //Use States
  const [dropDownValues, setDropDownValues] = useState<any>({
    TestsLookup: [],
    ToxPanelLookup: [],
    QCLevelsLookup: [],
    InstrumentLookup: [],
    SpecimentTypesLookup: [],
  });
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [bloodLeveyJenningsValues, setBloodLeveyJenningsValues] =
    useState<ToxLeveyJenningsValues>(initialToxLeveyJenning);
  const [filterDate, setFilterDate] = useState<any>();
  //Split date
  const dateRange = filterDate ? filterDate : "";
  const [startDate, endDate] = dateRange.split("to");
  const start = moment(startDate).format("MM/DD/YYYY");
  const end = moment(endDate).format("MM/DD/YYYY");

  const GetInstrumentsLookup = (reqtypeId: number, panelId: number) => {
    GetTOXAndBloodInstrumentsLookup(reqtypeId, panelId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          InstrumentLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const QCLevelsLookupApi = (reqTypeId: number, instrumentId: number) => {
    QCLevelsLookup(reqTypeId, instrumentId, 0, 0)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          QCLevelsLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const GetTestLookupApi = (
    specimenTypeId: number,
    panelId: number,
    reqTypeId: number
  ) => {
    GetTestsLookup(specimenTypeId, panelId, reqTypeId)
      .then((res: AxiosResponse) => {
        setDropDownValues((prev: any) => ({
          ...prev,
          TestsLookup: res?.data,
        }));
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const handleChange = (
    event: any,
    name?: string,
    isMulti: boolean = false
  ) => {
    // Case 1: Native or custom inputs using event.target
    if (event?.target) {
      const { name, value } = event.target;
      setBloodLeveyJenningsValues((prev: any) => ({
        ...prev,
        [name]: Number(value),
      }));
      return;
    }

    // Case 2: react-select (single select)
    if (!isMulti) {
      setBloodLeveyJenningsValues((prev: any) => ({
        ...prev,
        [name as string]: event?.value ?? 0,
      }));
      return;
    }

    // Case 3: react-select (multi select)
    if (isMulti) {
      setBloodLeveyJenningsValues((prev: any) => ({
        ...prev,
        [name as string]: event?.map((item: any) => item.value) ?? [],
      }));
    }
  };

  //Download Button
  const DownloadReport = async () => {
    const ObjToValidate = {
      instrumentId: bloodLeveyJenningsValues?.instrumentId,
      qcLevelIds: bloodLeveyJenningsValues?.qcLevelIds.length,
      fromDate: start === "Invalid date" ? "" : start,
      toDate: end === "Invalid date" ? "" : end,
      testConfigIds: bloodLeveyJenningsValues?.testConfigIds.length,
    };

    const { isValid, invalidField } = validateFields(ObjToValidate);
    if (isValid) {
      setIsDisabled(true);
      const payload = {
        reqTypeId: 3,
        panelTypeId: bloodLeveyJenningsValues?.panelTypeId,
        specimenTypeId: bloodLeveyJenningsValues?.specimenTypeId,
        instrumentId: bloodLeveyJenningsValues?.instrumentId,
        qcLevelIds: bloodLeveyJenningsValues?.qcLevelIds,
        fromDate: start,
        toDate: end,
        testConfigIds: bloodLeveyJenningsValues?.testConfigIds,
      };
      try {
        await LeveyJenningDownloadReport(payload).then((res: AxiosResponse) => {
          if (res?.data?.statusCode === 200) {
            toast.success(
              res?.data?.message || "Report generated successfully"
            );
            RequisitionType.ShowBlob(res?.data?.data).then((res: any) => {
              window.open(res?.data?.Data.replace("}", ""), "_blank");
            });
          } else if (res?.data?.statusCode === 400) {
            toast.error(res?.data?.message || "Error generating report");
            return;
          }
        });
      } catch (error) {
        console.trace(error);
      } finally {
        setIsDisabled(false);
      }
    } else {
      const errorMessage = invalidField
        ? t(`Please Fill ${queryDisplayTagNames[invalidField] ?? ""} field`)
        : t("Invalid field found");
      toast.error(t(errorMessage));
    }
  };

  useEffect(() => {
    GetInstrumentsLookup(2, 0);
    GetTestLookupApi(0, 0, 2);
  }, []);

  useEffect(() => {
    if (bloodLeveyJenningsValues?.instrumentId) {
      setBloodLeveyJenningsValues((prev: any) => ({
        ...prev,
        qcLevelIds: [],
      }));
      QCLevelsLookupApi(2, bloodLeveyJenningsValues?.instrumentId);
    }
  }, [bloodLeveyJenningsValues?.instrumentId]);

  return (
    <>
      <div className="d-flex flex-column flex-column-fluid py-2 py-lg-3">
        <div className="app-toolbar py-2 py-lg-3">
          <div className="app-container container-fluid d-flex flex-wrap gap-4 justify-content-center justify-content-sm-between align-items-center">
            <BreadCrumbs />
          </div>
        </div>
        <div className="app-toolbar py-2 py-lg-3">
          <div className="d-flex flex-column flex-column-fluid">
            <div className="app-content flex-column-fluid">
              <div className="app-container container-fluid">
                <div className="bg-white p-6">
                  <h3 className="mb-5">{t("Report")}</h3>
                  <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mb-2 col-12 responsive-flexed-actions">
                    <div className="d-flex gap-2 responsive-flexed-actions">
                      <div className="d-flex gap-2 gap-lg-3 justify-content-center justify-content-sm-start responsive-flexed-actions flex-wrap align-items-end">
                        <div className="mt-0">
                          <label className="required mb-2 fw-500">
                            {t("Instrument")}
                          </label>
                          <Select
                            menuPortalTarget={document.body}
                            theme={(theme) => styles(theme)}
                            placeholder="Instruments"
                            options={dropDownValues.InstrumentLookup}
                            styles={reactSelectSMStyle}
                            onChange={(event: any) =>
                              handleChange(event, "instrumentId")
                            }
                            value={dropDownValues.InstrumentLookup?.filter(
                              function (option: any) {
                                return (
                                  option.value ===
                                  bloodLeveyJenningsValues?.instrumentId
                                );
                              }
                            )}
                          />
                        </div>
                        <div className="mt-0" style={{ maxWidth: "30%" }}>
                          <label className="required mb-2 fw-500">
                            {t("QC Level")}
                          </label>
                          <Select
                            menuPortalTarget={document.body}
                            theme={(theme) => styles(theme)}
                            placeholder="Select QC Level"
                            options={dropDownValues.QCLevelsLookup}
                            isMulti
                            styles={reactSelectSMStyle}
                            onChange={(event: any) =>
                              handleChange(event, "qcLevelIds", true)
                            }
                            value={dropDownValues.QCLevelsLookup.filter(
                              (option: any) =>
                                bloodLeveyJenningsValues.qcLevelIds.includes(
                                  option.value
                                )
                            )}
                          />
                        </div>
                        <div className="align-items-center">
                          <label className="required mb-2 fw-500">
                            {t("Date Range")}
                          </label>
                          <div>
                            <DatePickerDuplicate
                              setFilterData={setFilterDate}
                              filterData={filterDate}
                            />
                          </div>
                        </div>
                        <PermissionComponent
                          moduleName="Blood LIS"
                          pageName="Levey Jenning Report"
                          permissionIdentifier="View"
                        >
                          {({ pageName, moduleName, permissionIdentifier }) => (
                            <button
                              className="btn btn-icon btn-sm fw-bold btn-warning"
                              onClick={DownloadReport}
                              disabled={isDisabled}
                              title={getPermissionDisplayName(pageName, moduleName, permissionIdentifier, t("View"))}
                            >
                              <i className="bi bi-cloud-download"></i>
                            </button>
                          )}
                        </PermissionComponent>
                      </div>
                    </div>
                  </div>
                  <hr />
                  <TestsSelection
                    testsLookup={dropDownValues.TestsLookup}
                    setLeveyJenningsValues={setBloodLeveyJenningsValues}
                    allTestsHeader={t("All Analytes")}
                    selectedTestsHeader={t("Selected Analytes")}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default BloodLeveyJenning;
